import type { VariantContent } from './types';

export const therapyContent: VariantContent = {
  id: 'therapy',

  tagline: ['愛され看板犬', 'デジタルセラピードッグ'],
  ctaBadge: '＼ まずはお気軽にご相談 ／',
  ctaText: '資料請求・お問い合わせ',
  ctaHref: '#contact',

  logoWebp: '/lp-img/therapy-logo.svg',
  logoWhiteSvg: '/lp-img/therapy-logo_white.svg',
  logoSignWhiteSvg: '/lp-img/therapy-logo_sign_white.svg',

  navItems: [
    { href: '#life', label: 'たっぷとの暮らし' },
    { href: '#about', label: 'たっぷとは' },
    { href: '#howto', label: 'みんなの使い方' },
    { href: '#pricing', label: '料金プラン' },
    { href: '#compare', label: '他社比較' },
    { href: '#flow', label: 'ご利用までの流れ' },
    { href: '#qa', label: 'よくあるご質問' },
    { href: '#contact', label: 'お問い合わせ' },
  ],

  showOption: false,
  showSupport: false,
  showCompare: true,
  showFraudStats: false,

  hero: {
    subtitle: ['愛され看板犬', 'デジタルセラピードッグ'],
    logoSvg: '/lp-img/therapy-logo.svg',
    logoAlt: 'たっぷ',
    images: [
      '/lp-img/therapy-hero-main.webp',
      '/lp-img/therapy-hero-slide2.webp',
      '/lp-img/therapy-hero-slide3.webp',
    ],
    slideAlt: 'セラピードッグたっぷと過ごす施設の日常',
    badges: [
      { label: '笑顔', sub: '創出' },
      { label: '会話', sub: '促進' },
      { label: '脳の', sub: '活性' },
    ],
    catchcopy: ['すべてを', '一匹のデジタル愛犬で'],
  },

  about: {
    paragraphs: [
      { lines: ['入居者に楽しく過ごしてほしい。', 'でもスタッフの手が足りない。', '看板犬を飼いたくてもお世話が負担。'] },
      { lines: ['デジタルなら、それが変わる。', 'たっぷは共用部の画面の中で暮らす、', '施設の看板犬です。'], bold: true },
    ],
    image: '/lp-img/therapy-life-photos.webp',
    imageAlt: 'たっぷと過ごす施設の日常',
  },

  life: {
    en: 'life',
    heading: ['施設の毎日に', 'もう一匹の仲間を'],
  },

  howto: {
    heading: 'こんな場面で',
    cards: [
      {
        number: '使い方①',
        title: '共用部がさみしいとき',
        description: '声にも顔にも反応するたっぷ。ごはんをあげれば喜び、撫でればしっぽを振る。その愛らしい反応が、入居者の自然な笑顔を引き出します。',
        color: '#da627d',
        icon: '/lp-img/therapy-howto-icon1.webp',
      },
      {
        number: '使い方②',
        title: '入居者同士の話題がほしいとき',
        description: '「今日たっぷがこんなことして」――たっぷの話題が入居者同士、スタッフ、面会のご家族との会話のきっかけになります。ひとりの時間にも聞き役に。',
        color: '#68b684',
        icon: '/lp-img/therapy-howto-icon2.webp',
      },
      {
        number: '使い方③',
        title: '入居者に役割を持ってほしいとき',
        description: 'ごはんをあげたり、撫でたり、声をかけたり。たっぷのお世話を通じて「育てる」という役割が生まれ、入居者の毎日に張り合いをもたらします。',
        color: '#ffa4d0',
        icon: '/lp-img/therapy-howto-icon3.webp',
      },
      {
        number: '使い方④',
        title: '外出できない日が続くとき',
        description: 'たっぷと一緒にバーチャル散歩へ。画面いっぱいに広がる景色の中を歩く疑似体験が、外に出られない日の気分をリフレッシュしてくれます。',
        color: '#da627d',
        icon: '/lp-img/therapy-howto-icon4.webp',
      },
    ],
  },

  pricing: {
    heroImage: '/lp-img/therapy-pricing-hero.webp',
    plans: [
      {
        id: 'signage',
        badge: 'サイネージ導入プラン',
        initialPrice: { label: '初期費用', value: '約80,000', large: false, unit: '円', note: 'サイネージモニター代＋出張設置サポート込み' },
        monthlyPrice: { label: '月額費用', value: '約5,000', accent: true, unit: '円', tax: true, note: '初回1カ月無料' },
        features: {
          title: '基本機能',
          items: [
            '育てる / お世話する / 可愛がる',
            'バーチャル散歩機能',
            '回想・コミュニケーション促進機能',
            '脳トレゲーム',
          ],
        },
        accordion: {
          title: '初回無料体験とは？',
          answer: '初回1カ月間は、すべての機能を完全無料でお試しいただけます。体験期間中の機能制限はありません。1カ月経過後、公式LINEまたはお電話にて継続のご意向を確認いたします。継続されない場合、返却時の送料も無料です。',
          note: '※2カ月目以降は、月額費用が発生します。',
        },
      },
    ],
    packageImage: '/lp-img/therapy-package-photo.webp',
    packageItems: [
      'サイネージタッチモニター',
      'スタンド（選択式）',
      '電源ケーブル',
      '取扱説明書・設置ガイド',
    ],
    productSubtitle: 'サイネージタッチモニターの詳細な特徴や仕様を説明します。',
    productDetails: [
      { item: 'サイズ', text: '約730 × 430 × 50mm（32インチ）' },
      { item: '重さ', text: '約5〜8kg' },
      { item: '解像度', text: 'FHD 1920×1080' },
      { item: 'OS', text: 'Android' },
      { item: '通信規格', text: 'Wi-Fi 2.4GHz / 5GHz デュアルバンド' },
      { item: '設置', text: 'VESA対応（壁掛け / スタンド選択式）' },
    ],
    notices: [
      '直射日光が当たる場所には設置しないでください。画面の視認性低下や故障の原因になります。',
      '随時アップデートで改善を続けています。万一不具合が発生した場合は、運営までご連絡いただければ、データを保持したまま対応いたします。',
      '常に最新のアプリバージョンをご利用ください。（アップデート時にはご案内を送付いたします。）',
    ],
    subsidyNote: '地域によって介護テクノロジー導入支援事業の対象となる場合があります。',
  },

  pricingPhoto: {
    roundTop: '/lp-img/round_bottom_pink.svg',
    image: '/lp-img/pricing-photo.webp',
    roundBottom: '/lp-img/round_top_white.svg',
  },

  compare: {
    en: 'compare',
    heading: '他のロボットペットとの違い',
    rows: [
      { label: '初期費用', tappu: '約80,000円（設置込み）', robotA: '約577,000円〜', robotB: '約400,000円' },
      { label: '月額費用', tappu: '約5,000円', robotA: '約15,000円〜', robotB: 'なし' },
      { label: '形態', tappu: 'サイネージ（画面型）', robotA: 'ぬいぐるみ型ロボット', robotB: 'アザラシ型ロボット' },
      { label: 'バーチャル散歩', tappu: '◎ 搭載', robotA: '× なし', robotB: '× なし' },
      { label: '衛生面', tappu: '◎ 画面のみ・清掃簡単', robotA: '△ 毛の清掃が必要', robotB: '○ 抗菌コート' },
      { label: '故障リスク', tappu: '○ 可動部なし', robotA: '△ 可動部あり', robotB: '△ 可動部あり' },
      { label: '補助金対象', tappu: '○ 対象となる場合あり', robotA: '○ 対象となる場合あり', robotB: '○ 対象となる場合あり' },
    ],
    footnote: '※ロボットA・ロボットBの価格・仕様は公開情報に基づく参考値です。',
  },

  flow: {
    steps: [
      {
        number: '1',
        title: 'お申し込み',
        description: 'アプリ、またはネットから直接お申し込みください。',
        link: { text: 'お申込みはこちら', href: '#contact' },
      },
      {
        number: '2',
        title: 'お届け',
        description: '数営業日以内に、セラピードッグのサイネージモニターキットを施設へ配送します。',
      },
      {
        number: '3',
        title: '設置',
        description: '設置サポートをお申込みの方には、設置スタッフが訪問。日時をご指定ください。お客様ご自身でも簡単に設置できます。',
      },
      {
        number: '4',
        title: '使い始め',
        description: 'セラピードッグのお名前を決めて、使い始めましょう！まずはお腹が空いているので、えさをあげて、愛情を与えましょう。',
      },
    ],
  },

  qa: {
    items: [
      { question: '契約の縛りや解約金はありますか？', answer: '縛りも違約金もありません。解約は電話・Web・公式LINEから可能です（解約月は満額・日割りなし）。' },
      { question: '導入前にお試しできますか？', answer: 'はい。初回1カ月間は無料でお試しいただけます。返却時の送料も無料です。' },
      { question: '施設のWiFi環境で動作しますか？', answer: 'はい。2.4GHz / 5GHz デュアルバンドのWiFiに対応しています。施設の既存WiFi環境でご利用いただけます。' },
      { question: '入居者の方でも使えますか？', answer: 'はい。入居者の方でも簡単にお使いいただけるシンプルな仕様です。' },
      { question: '故障したときは？', answer: '自然故障は無償交換（データは保持）。落下・水濡れなどの場合は有償です。' },
      { question: '入居者が壊してしまった場合は？', answer: '自然故障は無償交換いたします。落下や水濡れ等の場合は有償での交換となります。データはそのまま保持されます。' },
      { question: '見守りアプリで何がわかりますか？', answer: '施設スタッフやご家族がスマホで利用状況やたっぷに触れたログを確認でき、異変時はお知らせが届きます。' },
    ],
  },

  join: {
    en: 'contact',
    sectionId: 'contact',
    heading: 'お問い合わせ',
    subText: 'お気軽にご相談ください',
    ctaBadge: '＼ まずはお気軽にご相談 ／',
    ctaText: '資料請求・お問い合わせ',
    registrationSource: 'landing_page_therapy',
    deviceBannerNote: '※サイネージモニターの初期費用が発生します。',
    successMessage: '確認メールとパスワード設定メールを送信しました。',
  },

  footer: {
    dogImage: '/lp-img/therapy-footer-dog.webp',
    dogImageAlt: 'デジタルセラピードッグたっぷ',
    copyright: '© ぴちぴち株式会社 All Rights Reserved.',
  },

  seo: {
    title: 'デジタルセラピードッグ たっぷ | 介護施設向け看板犬サイネージ',
    description: 'たっぷは、介護施設の共用部で入居者の笑顔を生むデジタルセラピードッグ。会話促進・脳の活性・バーチャル散歩。初回1カ月無料体験。',
    ogTitle: 'デジタルセラピードッグ たっぷ | 介護施設向けセラピードッグ',
    ogDescription: '介護施設の共用部で入居者の笑顔を生むデジタルセラピードッグ。初回1カ月無料体験。',
    ogImage: 'https://digitalpet.jp/lp-img/therapy-hero-main.webp',
    twitterTitle: 'デジタルセラピードッグ たっぷ | 介護施設向け',
    twitterDescription: '介護施設の共用部で笑顔を生むデジタルセラピードッグ。初回1カ月無料。',
    canonical: 'https://digitalpet.jp/therapy',
  },
};
